import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, FileText, MessageSquare, Users, Shield, TrendingUp, CheckCircle, Sparkles, Upload, Lightbulb } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Landing() {
  const features = [
    {
      icon: FileText,
      title: 'Document Analysis',
      description: 'Upload project documents and supporting materials for comprehensive analysis.'
    },
    {
      icon: MessageSquare,
      title: 'AI-Powered Coaching',
      description: 'Get expert guidance based on proven change management frameworks like ADKAR and Kotter.'
    },
    {
      icon: Users,
      title: 'Stakeholder Strategy',
      description: 'Develop targeted engagement approaches for different stakeholder groups.'
    },
    {
      icon: Shield,
      title: 'Risk Identification',
      description: 'Proactively identify resistance points and mitigation strategies.'
    },
    {
      icon: TrendingUp,
      title: 'Track Progress',
      description: 'Maintain conversation history and track your change journey over time.'
    },
    {
      icon: CheckCircle,
      title: 'Actionable Insights',
      description: 'Receive practical, implementable recommendations for your specific context.'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      icon: Upload,
      title: 'Share Your Context',
      description: 'Tell us about your change initiative. If you have project documents you\'d like reviewed, feel free to share them - but they\'re completely optional.'
    },
    {
      step: '02',
      icon: MessageSquare,
      title: 'Chat With Your Coach',
      description: 'Have a conversation with your AI coach. Ask questions, explore challenges, and work through decisions together.'
    },
    {
      step: '03',
      icon: Lightbulb,
      title: 'Get Practical Guidance',
      description: 'Receive clear, actionable steps tailored to your situation. No theory overload—just what you need to move forward.'
    }
  ];

  const testimonials = [
    {
      quote: "Finally, a tool that understands the reality of leading change—not just the textbook version.",
      author: "Program Director",
      company: "Government Agency"
    },
    {
      quote: "It's like having a senior change consultant available 24/7. Helped me think through stakeholder resistance I hadn't considered.",
      author: "Transformation Lead",
      company: "Financial Services"
    },
    {
      quote: "Saved me hours of planning. The structured approach kept my project on track from day one.",
      author: "Project Manager",
      company: "Healthcare"
    }
  ];

  return (
    <div className="app-container bg-slate-900">
      {/* Hero Section */}
      <section className="hero-section">
        <div 
          className="hero-bg"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/2468056/pexels-photo-2468056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
        />
        <div className="hero-overlay" />
        
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium text-amber-400 bg-amber-400/10 rounded-full border border-amber-400/20">
              Strategic Leadership Partners
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Navigate Change<br />
              <span className="text-amber-400">With Confidence</span>
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
              Your AI-powered change coach. Upload your project documents and get practical guidance to help you deliver successful change.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 h-12 rounded-md hover-lift"
                  data-testid="hero-get-started-btn"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white px-8 h-12 rounded-md"
                  data-testid="hero-login-btn"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your Strategic Change Partner
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Leverage AI to accelerate your change initiatives with expert-level coaching and analysis.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="p-6 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors duration-200"
              >
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Get from stuck to sorted in three simple steps
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-6xl font-bold text-slate-800 mb-4">{item.step}</div>
                <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="h-7 w-7 text-slate-900" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Trusted by Change Leaders
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Hear from professionals navigating real-world transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="p-6 bg-white border border-slate-200 rounded-xl"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Sparkles key={i} className="h-4 w-4 text-amber-500" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Change Initiative?
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
              Join professionals who are successfully navigating organizational change with AI-powered coaching.
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-10 h-12 rounded-md hover-lift"
                data-testid="cta-get-started-btn"
              >
                Start Your Free Session
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-md flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-slate-900" />
            </div>
            <span className="font-semibold text-white">SLP Coach</span>
          </div>
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Strategic Leadership Partners
          </p>
        </div>
      </footer>
    </div>
  );
}
